/**
 * communication medium between controllers, commands and views
 */

import API = require("./app"); // typing only
import Portal = require("portal-gis");
import MapView = require("./views/MapView"); // typing only
import AppView = require("./views/AppLayoutView"); // typing only
import Backbone = require("backbone");
import Utils = require("./utils");
import ol = require("ol"); // typing only
import Controller = require("./controls/controller"); // typing only
import View = require("./views/base-view"); // typing only
import _ = require("underscore");
import Extensions = require("./data/extensions");

// ensures listeners remain compatible with this signature
interface IViewArgs {
  controller: Controller;
  control: Portal.ControlLeaf;
  view: View<any>;
}

class Mediator {
  public extensions: Extensions;
  public model: Backbone.Model;
  public events: Backbone.Events;

  static about = `
All messaging passes through this mediator.  Provides convenience functions for accessing shared state.
    `;

  constructor() {
    this.model = new Backbone.Model();
    this.events = _.extend({}, Backbone.Events);
    /// allow ad-hoc associations within the context of this mediator
    this.extensions = new Extensions();
  }

  destroy() {
    let appView = <AppView>this.model.get("appView");
    let mapView = <MapView>this.model.get("mapView");

    if (appView) {
      appView.destroy();
    }
    if (mapView) {
      mapView.destroy();
    }

    this.events.trigger("destroy-commands");

    this.events.unbind(); // unbind all mediator events
    this.model.unbind(); // unbind all model events
  }

  /* { API Starts */
  trigger(eventName: string, args: any);
  trigger(
    eventName: "view",
    args: {
      out: IViewArgs;
    }
  );
  trigger(eventName: string, args: any) {
    this.events.trigger(eventName, args);
  }

  on(eventName: string, cb: (args: any) => void);
  on(eventName: string, cb: (args: { out: any }) => void);
  on(eventName: "view", cb: (args: { out: IViewArgs }) => void);
  on(eventName: "command", cb: (args: { out: { command: Portal.CommandLeaf } }) => void);
  on(eventName: string, cb: Function) {
    this.events.on(eventName, cb);
    let stop = () => {
      this.events.off(eventName, cb);
    };
    return { off: stop };
  }

  get config() {
    if (!this.api) {
      console.warn("api is undefined");
      return null;
    }
    return this.api.configuration;
  }

  // strange to have an api.api; the mediator is the api, so what is this api?
  get api() {
    return <API>this.model.get("api");
  }

  mapView(cb: (view: MapView) => void) {
    Utils.once<MapView>(this.model, "mapView", cb);
  }

  mapViewMap(cb: (map: ol.Map) => void) {
    this.mapView((mapView) => Utils.once<ol.Map>(mapView.model, "map", cb));
  }

  appView(cb: (map: AppView) => void) {
    this.mapView((mapView) => Utils.once<AppView>(this.model, "appView", cb));
  }

  findLayer(id: string, layers?: Portal.MapLayerLeaf[]) {
    // doesn't properly type if this is set in prams
    layers = layers || this.api.layers.map((l) => l.data);

    let result = layers.filter((l) => l.id === id)[0];
    if (!result) {
      layers.some((l) => {
        result = l.Layers && this.findLayer(id, l.Layers.Layers);
        return !!result;
      });
    }
    return result;
  }

  /**
    Forces the existence of an extension container for an object
    @param o the object of interest
    @param [ext] sets these value on the extension object
    */
  extend(o: any, ext?) {
    return this.extensions.extend(o, ext);
  }

  /* API Ends } */
}

export = Mediator;
